// Вкладка "Потеря напряжения"

import { useCallback } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { InputPanel } from '../input/InputPanel';
import { NumberInput, SelectField, RadioGroup } from '../input';
import { Button, ExportButton } from '../buttons';
import { ResultPanel, StatusIndicator, SectionGrid } from '../results';
import { 
  STANDARD_SECTIONS, 
  NETWORK_TYPE_LABELS, 
  MATERIAL_LABELS, 
  LOAD_TYPE_LABELS,
  LINE_TYPE_LABELS,
} from '../../data/constants';
import { 
  calculateVoltageLoss, 
  selectSectionByVoltageLoss 
} from '../../utils/calculations';
import { Calculator, RotateCcw, Wrench } from 'lucide-react';
import type { NetworkType, Material, LoadType, LineType } from '../../types';

// Опции для селектов
const networkTypeOptions = Object.entries(NETWORK_TYPE_LABELS).map(([value, label]) => ({
  value: value as NetworkType,
  label
}));

const materialOptions = Object.entries(MATERIAL_LABELS).map(([value, label]) => ({
  value: value as Material,
  label
}));

const loadTypeOptions = Object.entries(LOAD_TYPE_LABELS).map(([value, label]) => ({
  value: value as LoadType,
  label
}));

const lineTypeOptions = Object.entries(LINE_TYPE_LABELS).map(([value, label]) => ({
  value: value as LineType,
  label
}));

export function VoltageLossTab() {
  const {
    input,
    voltageLossResult,
    setInput,
    setVoltageLossResult,
    reset,
  } = useCalculator();

  // Обработчик расчёта
  const handleCalculate = useCallback(() => {
    const result = calculateVoltageLoss(input);
    setVoltageLossResult(result);
  }, [input, setVoltageLossResult]);

  // Обработчик подбора сечения
  const handleSelectSection = useCallback(() => {
    const recommendedSection = selectSectionByVoltageLoss(input, STANDARD_SECTIONS);
    if (recommendedSection !== null) {
      setInput({ crossSection: recommendedSection });
      // Автоматически пересчитываем с новым сечением
      const newInput = { ...input, crossSection: recommendedSection };
      const result = calculateVoltageLoss(newInput);
      setVoltageLossResult(result);
    }
  }, [input, setInput, setVoltageLossResult]);

  // Обработчик сброса
  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  // Обработчик изменения сечения
  const handleSectionChange = useCallback((section: number) => {
    setInput({ crossSection: section });
  }, [setInput]);

  return (
    <div className="voltage-loss-tab">
      <div className="voltage-loss-tab__input">
        {/* Все параметры в одной панели */}
        <InputPanel title="Параметры расчёта">
          <div className="input-grid">
            <RadioGroup
              label="Тип сети"
              value={input.networkType}
              onChange={(value) => setInput({ networkType: value as NetworkType })}
              options={networkTypeOptions}
              tooltip="Выберите тип сети: однофазная (220В) или трёхфазная (380В)"
            />
            <NumberInput
              label="Напряжение"
              value={input.nominalVoltage}
              onChange={(value) => setInput({ nominalVoltage: value })}
              unit="В"
              min={0}
              step={1}
              required
              tooltip="Номинальное напряжение сети (обычно 220В или 380В)"
            />
            <NumberInput
              label="Ток"
              value={input.current}
              onChange={(value) => setInput({ current: value })}
              unit="А"
              min={0}
              step={0.1}
              required
              tooltip="Расчётный ток нагрузки в амперах"
            />
            <NumberInput
              label="Мощность"
              value={input.power ?? 0}
              onChange={(value) => setInput({ power: value > 0 ? value : null })}
              unit="кВт"
              min={0}
              step={0.1}
              tooltip="Мощность нагрузки (для автоматического расчёта тока)"
            />
            <NumberInput
              label="Длина"
              value={input.length}
              onChange={(value) => setInput({ length: value })}
              unit="м"
              min={0}
              step={1}
              required
              tooltip="Длина кабельной линии"
            />
            <NumberInput
              label="cos φ"
              value={input.powerFactor}
              onChange={(value) => setInput({ powerFactor: value })}
              unit=""
              min={0}
              max={1}
              step={0.01}
              required
              tooltip="Коэффициент мощности"
            />
            <SelectField
              label="Материал"
              value={input.material}
              onChange={(value) => setInput({ material: value as Material })}
              options={materialOptions}
            />
            <SelectField
              label="Тип линии"
              value={input.lineType}
              onChange={(value) => setInput({ lineType: value as LineType })}
              options={lineTypeOptions}
            />
            <SelectField
              label="Нагрузка"
              value={input.loadType}
              onChange={(value) => setInput({ loadType: value as LoadType })}
              options={loadTypeOptions}
            />
          </div>
        </InputPanel>

        {/* Выбор сечения */}
        <InputPanel title="Сечение жилы">
          <SectionGrid
            sections={STANDARD_SECTIONS}
            selectedSection={input.crossSection}
            onSectionChange={handleSectionChange}
          />
        </InputPanel>

        {/* Кнопки управления */}
        <div className="voltage-loss-tab__actions">
          <Button
            variant="primary"
            size="sm"
            icon={<Calculator size={16} />}
            onClick={handleCalculate}
            disabled={input.current <= 0 && (input.power === null || input.power <= 0)}
          >
            Расчёт
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Wrench size={16} />}
            onClick={handleSelectSection}
            disabled={input.current <= 0 && (input.power === null || input.power <= 0)}
          >
            Подобрать
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<RotateCcw size={16} />}
            onClick={handleReset}
          >
            Сброс
          </Button>
          <ExportButton
            mode="voltage"
            input={input}
            voltageLossResult={voltageLossResult}
          />
        </div>
      </div>

      {/* Панель результатов */}
      <div className="voltage-loss-tab__results">
        {voltageLossResult ? (
          <ResultPanel
            title="Результаты расчёта потери напряжения"
            status={
              <StatusIndicator status={voltageLossResult.isWithinNorm ? 'success' : 'danger'}>
                {voltageLossResult.isWithinNorm ? 'В пределах нормы' : 'Превышение нормы'}
              </StatusIndicator>
            }
            rows={[
              { 
                label: 'Потеря напряжения', 
                value: voltageLossResult.voltageLoss.toFixed(2), 
                unit: 'В' 
              },
              { 
                label: 'Потеря напряжения', 
                value: voltageLossResult.voltageLossPercent.toFixed(2), 
                unit: '%',
                highlight: voltageLossResult.isWithinNorm ? 'success' : 'danger'
              },
              { 
                label: 'Допустимая потеря', 
                value: voltageLossResult.maxAllowedLoss.toFixed(1), 
                unit: '%' 
              },
              { 
                label: 'Активное сопротивление', 
                value: voltageLossResult.resistance.toFixed(4), 
                unit: 'Ом' 
              },
              { 
                label: 'Реактивное сопротивление', 
                value: voltageLossResult.reactance.toFixed(4), 
                unit: 'Ом' 
              },
              { 
                label: 'Полное сопротивление', 
                value: voltageLossResult.impedance.toFixed(4), 
                unit: 'Ом' 
              },
            ]}
          />
        ) : (
          <div className="empty-state">
            <Calculator className="empty-state__icon" />
            <p className="empty-state__text">
              Введите параметры и нажмите "Рассчитать" для получения результатов
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default VoltageLossTab;
